import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import { Separator } from "../../../../ui/Separator";
import { DatePicker } from "../../../../ui/Datepicker/DatePicker";
import { t } from "i18next";
import { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { equipmentStatuses, moveStatuses } from "../../../../enums";
import { useFetch } from "../../../../hooks/useFetch";
import { useAppStore } from "../../../../stores/RootStore";
import { uploadUrl } from "@/config/publicConfig";
import { Equipment, EquipmentById, SelectOptions, Upload } from "../../../../types/types";
import { Input } from "../../../../ui/Input/Input";
import { Label } from "../../../../ui/Label/Label";
import { SelectButton } from "../../../../ui/SelectButton/SelectButton";
import { Button } from "../../../../ui/Button";
import EquipmentDelete from "./EquipmentDelete";

type Props = {
  currentEquipment: Equipment;
  setCurrentEquipment: (currentEquipment: Equipment) => void;
};

const MainTab = ({ currentEquipment, setCurrentEquipment }: Props) => {
  const { producers, equipmentModels, equipments, divisions, persons, currentUser, currentSite, setEquipments } = useAppStore();
  const isRestricted = !currentUser?.attributes.admin && currentSite?.user_status == 'user';
  const producersList: SelectOptions = producers?.map((item) => ({ label: item.attributes.name, value: item.id }));
  const statusesList: SelectOptions = equipmentStatuses.map((status) => ({ label: t(status), value: status }));
  const moveStatusList: SelectOptions = moveStatuses.map((moveStatus) => ({
    label: t("move_" + moveStatus),
    value: moveStatus,
  }));

  const divisionsList: SelectOptions = divisions?.map((division) => ({
    label: division.attributes.name,
    value: String(division.id),
  }));

  const personsList: SelectOptions = persons?.map((person) => ({
    label: `${person.attributes.last_name} ${person.attributes.first_name} ${person.attributes.middle_name}`,
    value: String(person.id),
  }));

  const equipmentsList: SelectOptions = equipments?.map((equipment) => ({
    label: equipment.attributes.name,
    value: equipment.id,
  }));

  const [producerModelsList, setProducerModelList] = useState<SelectOptions>(
    equipmentModels
      ?.filter((model) => model.attributes.producer_id === Number(currentEquipment?.attributes.producer_id))
      .map((i) => ({ label: i.attributes.name, value: String(i.id) }))
  );

  const { handleSubmit, control, reset, setValue, getValues } = useForm({
    defaultValues: {
      name: currentEquipment.attributes.name,
      inventory_number: currentEquipment.attributes.inventory_number,
      factory_number: currentEquipment.attributes.factory_number,
      producer_id: currentEquipment.attributes.producer_id,
      equipment_model_id: currentEquipment.attributes.equipment_model_id,
      stop_threshold: currentEquipment.attributes.stop_threshold,
      balance_date: currentEquipment.attributes.balance_date
        ? new Date(currentEquipment.attributes.balance_date)
        : undefined,
      release_date: currentEquipment.attributes.release_date
        ? new Date(currentEquipment.attributes.release_date)
        : undefined,
      status: currentEquipment.attributes.status,
      move_status: currentEquipment.attributes.move_status,
      division_id: currentEquipment.attributes.division_id
        ? { value: currentEquipment.attributes.division_id, label: currentEquipment.attributes.division_name }
        : undefined,
      responsible_id: currentEquipment.attributes.responsible_id,
      parent_id: currentEquipment.attributes.parent_id,
    },
  });

  const equipmentData = currentEquipment?.attributes;
  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [{ response: itemUpdateResponse, isLoading: itemUpdateIsLoading, error: itemUpdateError }, doFetchItemUpdate] =
    useFetch(`/directories/equipment_items/${currentEquipment.id}`);
  const [{ response: saveChangeResponse, isLoading: saveChangeIsLoading, error: saveChangeError }, doFetchSaveChange] =
    useFetch(`/directories/equipment_items/${currentEquipment.id}/changes`);
  const [{ response: fileUploadResponse, isLoading: fileUploadIsLoading, error: fileUploadError }, doFetchFileUpload] =
    useFetch(`/uploads`, true, true);

  const editEquipmentItemAfterChange = () => {
    const newCurrentItemData = currentEquipment as unknown as Equipment;
    const withoutCurrentItem = equipments?.filter((equipment) => equipment.id != currentEquipment?.id)!;
    setEquipments([...withoutCurrentItem, newCurrentItemData]);
  };

  useEffect(() => {
    if (!saveChangeResponse) return;
    editEquipmentItemAfterChange();
  }, [saveChangeResponse]);

  // Загрузка паспорта оборудования и сертификата
  const [currentFileField, setCurrentFileField] = useState("");
  const [passport, setPassport] = useState<Upload>();
  const [certificate, setCertificate] = useState<Upload>();

  useEffect(() => {
    setPassport(equipmentData?.passport?.data as unknown as Upload);
    setCertificate(equipmentData?.certificate?.data as unknown as Upload);
  }, []);

  useEffect(() => {
    if (!fileUploadResponse) {
      return;
    }
    if (currentFileField === "passport") {
      setPassport(fileUploadResponse.data as unknown as Upload);
    }
    if (currentFileField === "certificate") {
      setCertificate(fileUploadResponse.data as unknown as Upload);
    }
  }, [fileUploadResponse, fileUploadIsLoading]);

  const onFileChoice = (type: string, file: string | Blob | File) => {
    setCurrentFileField(type);
    const formData = new FormData();
    formData.set("attachment", file);
    doFetchFileUpload({
      method: "POST",
      data: formData,
    });
  };

  const onSubmit = (formData: any) => {
    onUpdate({
      name: formData.name,
      factory_number: formData.factory_number,
      inventory_number: formData.inventory_number,
      producer_id: formData.producer_id,
      equipment_model_id: formData.equipment_model_id,
      balance_date: formData.balance_date,
      release_date: formData.release_date,
      //service_interval: formData.service_interval,
      //qrcode: formData.qrcode,
      stop_threshold: formData.stop_threshold,
      passport: { data: passport },
      certificate: { data: certificate },
    });
  };

  const onUpdate = (formData: any) => {
    doFetchItemUpdate({
      method: "PUT",
      data: {
        data: formData,
      },
    });
  };

  const editEquipmentItem = (itemSaveResponse: { data: unknown }) => {
    const newEquipmentData = itemSaveResponse.data as unknown as Equipment;
    const withoutEquipment = equipments?.filter((equipment) => equipment.id != currentEquipment.id)!;
    setEquipments([...withoutEquipment, newEquipmentData]);
  };

  useEffect(() => {
    if (!itemUpdateResponse) return;
    editEquipmentItem(itemUpdateResponse);
  }, [itemUpdateResponse]);

  function handleChangeProducer(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value?.value);
    setValue("equipment_model_id", null);
    setProducerModelList(
      equipmentModels
        ?.filter((model) => model.attributes.producer_id === Number(value?.value))
        .map((i) => ({ label: i.attributes.name, value: String(i.id) }))
    );
  }

  function handleChangeModel(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value?.value);
  }

  function handleChangeDivision(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value);

    let date = new Date().toISOString();
    setCurrentEquipment({
      ...currentEquipment,
      attributes: {
        ...currentEquipment?.attributes,
        division_id: value ? Number(value?.value) : null,
        division_name: value ? value.label : null,
        division_changed_at: date,
      },
    });
    doFetchSaveChange({
      method: "POST",
      data: { data: { division_id: value?.value || null, at: date } },
    });
  }

  function handleChangeStatus(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value?.value);

    let date = new Date().toISOString();
    setCurrentEquipment({
      ...currentEquipment,
      attributes: {
        ...currentEquipment?.attributes,
        status: value?.value || null,
        status_changed_at: date,
      },
    });
    doFetchSaveChange({
      method: "POST",
      data: { data: { status: value?.value || null, at: date } },
    });
  }

  function handleChangeMoveStatus(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value?.value);

    let date = new Date().toISOString();
    setCurrentEquipment({
      ...currentEquipment,
      attributes: {
        ...currentEquipment?.attributes,
        move_status: value?.value || null,
        move_status_changed_at: date,
      },
    });
    doFetchSaveChange({
      method: "POST",
      data: { data: { move_status: value?.value || null, at: date } },
    });
  }

  function handleChangeResponsible(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value?.value);

    let date = new Date().toISOString();
    setCurrentEquipment({
      ...currentEquipment,
      attributes: {
        ...currentEquipment?.attributes,
        responsible_id: Number(value?.value) || null,
        responsible_name: value ? value.label : null,
        responsible_changed_at: date,
      },
    });
    doFetchSaveChange({
      method: "POST",
      data: { data: { responsible_id: value?.value || null, at: date } },
    });
  }

  function handleChangeRelatatedEquipment(
    value: { value: string; label: string } | null,
    onChange: { (...event: any[]): void; (arg0: any): void }
  ) {
    onChange(value?.value);

    let date = new Date().toISOString();
    setCurrentEquipment({
      ...currentEquipment,
      attributes: {
        ...currentEquipment?.attributes,
        parent_id: Number(value?.value) || null,
        // position: value ? value.label : null,
        responsible_changed_at: date,
      },
    });
    doFetchSaveChange({
      method: "POST",
      data: { data: { parent_id: value?.value || null, at: date } },
    });
  }

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-background">
      <div className="flex flex-none w-full h-24 text-base gap-2 items-center justify-between px-6">
        <div className="flex flex-col space-y-1">
          <span className="text-2xl font-semibold">{t('equipment_settings')}</span>
        </div>
      </div>
      
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 max-w-[1008px]">
          <div className="flex flex-col gap-2">
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={t("equipment_name")} />
                </div>
              )}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-8">
              <Controller
                name="inventory_number"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Label>{t("equipment_inventory_number")}*</Label>
                    <Input {...field} />
                  </div>
                )}
              />
              <Controller
                name="factory_number"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Label>{t("equipment_factory_number")}*</Label>
                    <Input {...field} />
                  </div>
                )}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-8">
              <Controller
                name="producer_id"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("equipment_producer")}*</Label>
                    <SelectButton
                      label={t("choose_producer")}
                      searchable={true}
                      options={producersList!}
                      value={value ? producersList?.find((s) => String(s.value) == String(value))! : null}
                      onChange={(e) => handleChangeProducer(e, onChange)}
                    />
                  </div>
                )}
              />
              <Controller
                name="equipment_model_id"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("equipment_model")}*</Label>
                    <SelectButton
                      label={t("choose_model")}
                      searchable={true}
                      options={producerModelsList!}
                      value={value ? producerModelsList?.find((s) => s.value == String(value))! : null}
                      onChange={(e) => handleChangeModel(e, onChange)}
                    />
                  </div>
                )}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-8">
              <Controller
                name="status"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("status")}</Label>
                    <SelectButton
                      label={t("status_no_choosen")}
                      options={statusesList}
                      value={value ? statusesList.find((s) => s.value == value)! : null}
                      onChange={(e) => handleChangeStatus(e, onChange)}
                    />
                  </div>
                )}
              />
              <Controller
                name="move_status"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("move_status")}</Label>
                    <SelectButton
                      label={t("move_status_no_choosen")}
                      options={moveStatusList}
                      value={value ? moveStatusList.find((s) => s.value == value)! : null}
                      onChange={(e) => handleChangeMoveStatus(e, onChange)}
                    />
                  </div>
                )}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-8">
              <Controller
                name="division_id"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("division")}*</Label>
                    <SelectButton
                      label={t("no_division")}
                      searchable={true}
                      options={divisionsList!}
                      value={value?.value ? divisionsList?.find((s) => String(s.value) == String(value.value))! : null}
                      onChange={(e) => handleChangeDivision(e, onChange)}
                    />
                  </div>
                )}
              />
              <Controller
                name="responsible_id"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("responsible")}*</Label>
                    <SelectButton
                      label={t("no_persons")}
                      searchable={true}
                      options={personsList!}
                      value={value ? personsList?.find((s) => String(s.value) == String(value))! : null}
                      onChange={(e) => handleChangeResponsible(e, onChange)}
                    />
                  </div>
                )}
              />
            </div>
            {/* <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-8">
              <Controller
                name="stop_threshold"
                control={control}
                render={({ field: { value, onBlur, onChange, ref } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("stop_threshold")}*</Label>
                    <Input
                      disabled={isRestricted}
                      type="number"
                      value={value ? value : ""}
                      {...ref}
                      onBlur={onBlur}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
              <Controller
                name="parent_id"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("in_parent_equipment")}*</Label>
                    <SelectButton
                      label={t("no_related_equipment")}
                      searchable={true}
                      options={equipmentsList!}
                      value={value ? equipmentsList?.find((s) => String(s.value) == String(value))! : null}
                      onChange={(e) => handleChangeRelatatedEquipment(e, onChange)}
                    />
                  </div>
                )}
              />
            </div> */}
            {/* <Separator />
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-8">
              <Controller
                name="balance_date"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("balance_date")}</Label>
                    <DatePicker mode="single" selected={value} onSelect={onChange} />
                  </div>
                )}
              />
              <Controller
                name="release_date"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("release_date")}</Label>
                    <DatePicker mode="single" selected={value} onSelect={onChange} />
                  </div>
                )}
              />
            </div> */}
            {/* <Separator />
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-8">
              <div className="relative space-y-2">
                <Label htmlFor="file_input">{t("passport")}</Label>
                {passport ? (
                  <a href={uploadUrl(passport.id)} target="_blank">
                    {passport.attributes.filename}
                  </a>
                ) : (
                  <input
                    type="file"
                    className="block w-full text-sm text-bg-foreground
                file:me-4 file:py-2 file:px-4
                file:rounded file:border-0
                file:text-sm file:font-semibold
                file:bg-secondary file:text-secondary-foreground
                hover:file:bg-primary
                file:disabled:opacity-50 file:disabled:pointer-events-none"
                    //@ts-ignore 
                    onChange={(evt) => onFileChoice("passport", evt?.target?.files[0]!)}
                  />
                )}
              </div>
              <div className="relative space-y-2">
                <Label htmlFor="file_input">{t("certificate")}</Label>
                {certificate ? (
                  <a href={uploadUrl(certificate.id)} target="_blank">
                    {certificate.attributes.filename}
                  </a>
                ) : (
                  <input
                    type="file"
                    className="block w-full text-sm text-bg-foreground
                file:me-4 file:py-2 file:px-4
                file:rounded file:border-0
                file:text-sm file:font-medium
                file:bg-secondary file:text-secondary-foreground
                hover:file:bg-primary
                file:disabled:opacity-50 file:disabled:pointer-events-none"
                    //@ts-ignore 
                    onChange={(evt) => onFileChoice("certificate", evt?.target?.files[0]!)}
                  />
                )}
              </div>
            </div> */}
            <Separator />
            <div className="mt-8 flex w-full justify-between mb-20">
              <Button variant={"destructive"} size={"sm"} type="button" onClick={() => openDeleteModal()}>
                {t("delete")}
              </Button>
              <Button variant={"default"} size={"sm"} type="submit">{t("save")}</Button>
            </div>
          </div>
        </form>
      {isDeleteOpen && <EquipmentDelete id={currentEquipment.id} isDeleteOpen={isDeleteOpen} closeModal={closeDeleteModal} />}
    </div>
  );
};

export default MainTab;
